import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import axios from "axios";
import { FaPhoneAlt, FaWhatsapp, FaSearch, FaTimes } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from "../../Constants";

const PAGE_SIZE = 50;

const AVATAR_COLORS = [
  "from-indigo-500 to-purple-600",
  "from-pink-500 to-rose-600",
  "from-emerald-500 to-teal-600",
  "from-amber-500 to-orange-600",
  "from-sky-500 to-blue-600",
  "from-fuchsia-500 to-purple-600",
];

const getInitials = (name = "") => {
  const clean = name.trim();
  if (!clean || clean.toLowerCase() === "unknown") return "?";
  const parts = clean.split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const getColor = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export default function Contact() {
  const { user } = useContext(AuthContext);

  const [contacts, setContacts] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const loadingRef = useRef(false);
  const loaderRef = useRef(null);

  const fetchContacts = useCallback(
    async (cursor = null) => {
      if (!user?.id || loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      setError("");

      try {
        const params = { limit: PAGE_SIZE };
        if (cursor) params.cursor = cursor;

        const res = await axios.get(`${API_URL}/api/contacts/${user.id}`, {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        const { items = [], nextCursor: newCursor = null, hasMore: more = false } = res.data || {};

        setContacts((prev) => {
          if (!cursor) return items;
          const seen = new Set(prev.map((c) => c.leadId));
          return [...prev, ...items.filter((c) => !seen.has(c.leadId))];
        });
        setNextCursor(newCursor);
        setHasMore(!!more && !!newCursor);
      } catch (err) {
        console.error("Error fetching contacts:", err);
        setError("Failed to load contacts. Please try again.");
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [user?.id]
  );

  // Initial load
  useEffect(() => {
    setContacts([]);
    setNextCursor(null);
    setHasMore(true);
    fetchContacts(null);
  }, [fetchContacts]);

  // Infinite scroll
  useEffect(() => {
    const node = loaderRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingRef.current && nextCursor) {
          fetchContacts(nextCursor);
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, nextCursor, fetchContacts]);

  const term = search.trim().toLowerCase();
  const filtered = term
    ? contacts.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(term) ||
          (c.phone || "").includes(term)
      )
    : contacts;

  const showSkeleton = loading && contacts.length === 0;

  return (
    <div className="p-4 sm:p-5 bg-[#FAF8FF] cfg-font dark:bg-gray-900 dark:text-gray-100 min-h-screen rounded-lg shadow-lg">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Aerial:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;color:#1F2937;} .cfg-font, .cfg-font *{font-family:'Inter',sans-serif;} .cfg-font .cfg-heading{font-family:'Sora',sans-serif;}`}</style>

      {/* Header */}
      <div className="flex flex-col gap-4 mb-5 sm:flex-row sm:justify-between sm:items-center">
        <div className="flex flex-col">
          <h2 className="text-xl font-bold cfg-heading dark:text-white">
            Contacts
          </h2>
          <p className="text-xs text-[#6B7280] dark:text-gray-400 mt-1">
            {contacts.length} contacts loaded
            {hasMore && contacts.length > 0 ? " · scroll for more" : ""}
          </p>
        </div>

        <div className="relative w-full sm:w-72">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-xs" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or phone"
            className="w-full pl-9 pr-8 py-2 text-xs border border-[#E5E7EB] rounded-md bg-white text-[#374151] dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#E9D5FF] focus:border-[#673AB7] transition"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#374151]"
              title="Clear"
            >
              <FaTimes className="text-xs" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-[#E5E7EB] dark:border-gray-700">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-[#F3F4F6] dark:bg-gray-700 dark:text-white text-left text-[#6B7280]">
              <th className="p-3 border-b border-[#E5E7EB] dark:border-gray-600 font-semibold uppercase tracking-wider w-16">
                S No.
              </th>
              <th className="p-3 border-b border-[#E5E7EB] dark:border-gray-600 font-semibold uppercase tracking-wider">
                Name
              </th>
              <th className="p-3 border-b border-[#E5E7EB] dark:border-gray-600 font-semibold uppercase tracking-wider">
                Phone
              </th>
              <th className="p-3 border-b border-[#E5E7EB] dark:border-gray-600 font-semibold uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {showSkeleton &&
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-b border-[#E5E7EB] dark:border-gray-600">
                  <td className="p-3"><div className="h-3 w-6 bg-[#E5E7EB] rounded animate-pulse" /></td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#E5E7EB] animate-pulse" />
                      <div className="h-3 w-40 bg-[#E5E7EB] rounded animate-pulse" />
                    </div>
                  </td>
                  <td className="p-3"><div className="h-3 w-28 bg-[#E5E7EB] rounded animate-pulse" /></td>
                  <td className="p-3"><div className="h-6 w-20 bg-[#E5E7EB] rounded-full animate-pulse" /></td>
                </tr>
              ))}

            {filtered.map((c, i) => {
              const isUnknown = !c.name || c.name.toLowerCase() === "unknown";
              return (
                <tr
                  key={c.leadId}
                  className="border-b border-[#E5E7EB] dark:border-gray-600 hover:bg-[#F3F4F6] dark:hover:bg-gray-700 transition-colors duration-150"
                >
                  <td className="p-3 align-middle text-[#374151] dark:text-gray-200">
                    {i + 1}
                  </td>

                  <td className="p-3 align-middle text-[#374151] dark:text-gray-200 font-medium">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 shrink-0 rounded-full bg-gradient-to-br ${getColor(
                          c.name || c.phone
                        )} text-white flex items-center justify-center font-semibold text-[11px]`}
                      >
                        {getInitials(c.name)}
                      </div>
                      <span>{isUnknown ? "Unknown" : c.name}</span>
                    </div>
                  </td>

                  <td className="p-3 align-middle text-[#374151] dark:text-gray-200">
                    {c.phone}
                  </td>

                  <td className="p-3 align-middle">
                    <div className="flex items-center gap-2 flex-wrap">
                      <a
                        href={`tel:${c.phone}`}
                        title="Call"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F3E8FF] text-[#673AB7] border border-[#E9D5FF] text-[11px] font-medium hover:bg-[#E9D5FF] transition"
                      >
                        <FaPhoneAlt size={10} />
                        Call
                      </a>
                      <a
                        href={`https://wa.me/91${c.phone}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="WhatsApp"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-medium hover:bg-emerald-100 transition"
                      >
                        <FaWhatsapp size={12} />
                        WhatsApp
                      </a>
                    </div>
                  </td>
                </tr>
              );
            })}

            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan="4" className="text-center p-8 text-[#6B7280]">
                  {error || (term ? "No contacts match your search" : "No records found")}
                  {error && (
                    <div>
                      <button
                        onClick={() => fetchContacts(null)}
                        className="mt-3 px-3 py-1.5 text-xs bg-[#673AB7] text-white font-medium rounded-md hover:bg-[#5E35A6] transition"
                      >
                        Try again
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Infinite-scroll sentinel */}
        <div
          ref={loaderRef}
          className="py-4 text-center text-xs text-[#6B7280] dark:text-gray-400 border-t border-[#E5E7EB] dark:border-gray-700"
        >
          {loading && contacts.length > 0 && "Loading more..."}
          {!loading && error && contacts.length > 0 && (
            <button
              onClick={() => fetchContacts(nextCursor)}
              className="px-3 py-1.5 text-xs bg-[#673AB7] text-white font-medium rounded-md hover:bg-[#5E35A6] transition"
            >
              Retry
            </button>
          )}
          {!loading && !error && !hasMore && contacts.length > 0 && "You've reached the end"}
        </div>
      </div>
    </div>
  );
}